import { useMemo } from 'react'

import { useModals, useNumericParams, useSetState } from '@domain-group/react-hooks'

import { ModalContext } from '@domain-group/suit-theme'

import PageContext from '@contexts/pageContext'

import handleCallbackAction from '@functions/handleCallbackAction'
import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import type { ShowCreateOrEditWebhookModalPayload } from '@hooks/useWebhook'

import CreateOrEditWebhookModal from '@modals/CreateOrEditWebhookModal'

import PageContent from './components/PageContent'

const defaultState = {
  showCreateOrEditWebhookModal: false,
}

type SetState = (state: Partial<typeof defaultState>) => void

const callbacks = <ModalName extends keyof typeof componentCallbacks>(component: ModalName, setState: SetState) => {
  const componentCallbacks = {
    CreateOrEditWebhookModal: {
      closeModal: () => setState({ showCreateOrEditWebhookModal: false }),
      createWebhook: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      deleteWebhook: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      updateWebhook: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
  }

  return componentCallbacks[component] || {}
}

const Relations = () => {
  const [state, setState] = useSetState(defaultState)
  const {
    showCreateOrEditWebhookModal,
  } = state

  const { systemId } = useNumericParams()

  const modalContext = useModals({ setState })
  const {
    callbacks: { setModalData },
  } = modalContext

  const pageContext = useMemo(() => ({
    callbacks: {
      deleteWebhook: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      showCreateOrEditWebhookModal: (payload: ShowCreateOrEditWebhookModalPayload) => {
        setModalData('CreateOrEditWebhookModal', payload)
        setState({ showCreateOrEditWebhookModal: true })
      },
    },
  }), [])

  return (
    <PageContext.Provider value={pageContext}>
      <ModalContext.Provider value={modalContext}>
        <PageContent systemId={systemId} />

        <CreateOrEditWebhookModal
          callbacks={callbacks('CreateOrEditWebhookModal', setState)}
          showModal={showCreateOrEditWebhookModal}
        />
      </ModalContext.Provider>
    </PageContext.Provider>
  )
}

export default Relations
